import { useState, useEffect, useCallback } from 'react';
import { X, Save, Loader2, AlertCircle, RotateCcw, Star, Clock, ChevronDown, Hash, BarChart3, Settings } from 'lucide-react';
import { groupsAPI } from '../../../../../services';
import { ALLOWED_LEVERAGE, CATEGORY_LEVERAGE_LIMITS } from './GroupFormShared/shared/constants';
import './../css/edit-modal.css';

const ASSET_CATEGORIES = [
  { key: 'stocks',      label: 'Stocks',       icon: '📈', max: 50  },
  { key: 'crypto',      label: 'Crypto',       icon: '₿',   max: 100 },
  { key: 'indices',     label: 'Indices',      icon: '📊', max: 200 },
  { key: 'commodities', label: 'Commodities',  icon: '🛢️', max: 100 },
  { key: 'forex',       label: 'Forex',        icon: '💱',  max: 500 },
];

const SWAP_DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];

function extract(obj, field, fallback) {
  if (obj?.[field] !== undefined && obj?.[field] !== null) return obj[field];
  if (obj?.group?.[field] !== undefined) return obj.group[field];
  if (obj?.data?.[field] !== undefined) return obj.data[field];
  return fallback;
}

function isEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export default function EditGroupModal({ groupId, onSuccess, onClose }) {
  // ── State ──
  const [form, setForm] = useState(null);
  const [original, setOriginal] = useState(null);
  const [isDefault, setIsDefault] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const changed = original && !isEqual(form, original);

  // ── Fetch group on mount ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setFetching(true);
        setFetchError('');
        const result = await groupsAPI.getGroupDetails(groupId);
        let data = result;
        if (result?.data?.data?.group) data = result.data.data.group;
        else if (result?.data?.data) data = result.data.data;
        else if (result?.data) data = result.data;

        const ls = extract(data, 'leverageSettings', {});
        const normalized = {
          name: extract(data, 'name', ''),
          description: extract(data, 'description', ''),
          leverageSettings: {
            stocks:      { maxLeverage: ls.stocks?.maxLeverage ?? ls.stocks?.max ?? 50,     currentLeverage: ls.stocks?.currentLeverage ?? ls.stocks?.default ?? 10 },
            crypto:      { maxLeverage: ls.crypto?.maxLeverage ?? ls.crypto?.max ?? 100,     currentLeverage: ls.crypto?.currentLeverage ?? ls.crypto?.default ?? 30 },
            indices:     { maxLeverage: ls.indices?.maxLeverage ?? ls.indices?.max ?? 200,   currentLeverage: ls.indices?.currentLeverage ?? ls.indices?.default ?? 75 },
            commodities: { maxLeverage: ls.commodities?.maxLeverage ?? ls.commodities?.max ?? 100, currentLeverage: ls.commodities?.currentLeverage ?? ls.commodities?.default ?? 25 },
            forex:       { maxLeverage: ls.forex?.maxLeverage ?? ls.forex?.max ?? 500,       currentLeverage: ls.forex?.currentLeverage ?? ls.forex?.default ?? 200 },
          },
          commission:         extract(data, 'commission', 0.0005),
          swap:               extract(data, 'swap', 0.001),
          swapEnabled:        extract(data, 'swapEnabled', false),
          swapTime:           extract(data, 'swapTime', '22:00'),
          tripleSwapDay:      extract(data, 'tripleSwapDay', 'Wednesday'),
          defaultSpread:      extract(data, 'defaultSpread', 0.00005),
          maxPositionSize:    extract(data, 'maxPositionSize', 500000),
          allowedInstruments: extract(data, 'allowedInstruments', []),
        };
        if (!cancelled) {
          setForm(normalized);
          setOriginal(JSON.parse(JSON.stringify(normalized)));
          setIsDefault(!!(data.isDefault || extract(data, 'isDefault', false)));
        }
      } catch (err) {
        if (!cancelled) setFetchError(err.message || 'Failed to load group');
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();
    return () => { cancelled = true; };
  }, [groupId]);

  // ── Escape key ──
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && !saving) handleClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [saving, changed]);

  // ── Handlers ──
  const updateField = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setFieldErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
  }, []);

  const updateLeverage = useCallback((category, field, value) => {
    setForm(prev => ({
      ...prev,
      leverageSettings: {
        ...prev.leverageSettings,
        [category]: { ...prev.leverageSettings[category], [field]: value },
      },
    }));
    setFieldErrors(prev => { const n = { ...prev }; delete n[`lev_${category}_${field}`]; return n; });
  }, []);

  const handleClose = () => {
    if (saving) return;
    if (changed && !window.confirm('You have unsaved changes. Close anyway?')) return;
    onClose();
  };

  const handleReset = () => {
    if (original) setForm(JSON.parse(JSON.stringify(original)));
    setFieldErrors({});
    setSubmitError('');
  };

  // ── Submit ──
  const handleSubmit = async () => {
    // Validate
    const errors = {};
    if (!form.name?.trim()) errors.name = 'Group name is required';
    else if (form.name.length < 3) errors.name = 'Name must be at least 3 characters';
    else if (form.name.length > 50) errors.name = 'Name must be under 50 characters';

    if (form.description && form.description.length > 500) errors.description = 'Description too long (max 500)';
    if (form.commission < 0 || form.commission > 0.1) errors.commission = 'Must be 0–10%';
    if (form.swap < 0 || form.swap > 0.1) errors.swap = 'Must be 0–10%';
    if (form.swapEnabled && form.swapTime && !/^([01]\d|2[0-3]):[0-5]\d$/.test(form.swapTime)) errors.swapTime = 'Use HH:mm format';
    if (form.defaultSpread < 0 || form.defaultSpread > 0.01) errors.defaultSpread = 'Must be 0–1%';
    if (form.maxPositionSize < 1000 || form.maxPositionSize > 10000000) errors.maxPositionSize = 'Must be $1K–$10M';

    // Leverage validation (simplified — only warn, don't block)
    ASSET_CATEGORIES.forEach(({ key, max }) => {
      const s = form.leverageSettings[key];
      if (!s) return;
      if (!ALLOWED_LEVERAGE.includes(s.maxLeverage)) errors[`lev_${key}_maxLeverage`] = `Must be a preset value`;
      if (s.maxLeverage > max) errors[`lev_${key}_maxLeverage`] = `Max ${max}:1`;
      if (s.currentLeverage > s.maxLeverage) errors[`lev_${key}_currentLeverage`] = `Cannot exceed max leverage`;
      if (!ALLOWED_LEVERAGE.includes(s.currentLeverage)) errors[`lev_${key}_currentLeverage`] = `Must be a preset value`;
    });

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      // Scroll to first error
      const first = document.querySelector('.em-field-error');
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSaving(true);
    setSubmitError('');
    try {
      let payload = { ...form };
      // Remove readonly fields for default group
      if (isDefault) {
        delete payload.name;
        delete payload.description;
      }
      const result = await groupsAPI.updateGroup(groupId, payload);
      onSuccess?.(result);
      onClose();
    } catch (err) {
      setSubmitError(err.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Leverage preset helpers ──
  const getPresetOptions = (category) => {
    const max = CATEGORY_LEVERAGE_LIMITS[category] || 500;
    return ALLOWED_LEVERAGE.filter(v => v <= max);
  };

  const isFieldChanged = (a, b) => !isEqual(a, b);
  const fieldClass = (originalVal, currentVal) =>
    `em-input${isFieldChanged(originalVal, currentVal) ? ' em-input-changed' : ''}`;

  // ── Fetching state ──
  if (fetching) {
    return (
      <div className="em-overlay">
        <div className="em-backdrop" />
        <div className="em-loading-box">
          <div className="gm-spinner" />
          <span className="gm-loading-text">Loading group details…</span>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="em-overlay">
        <div className="em-backdrop" onClick={onClose} />
        <div className="em-loading-box">
          <AlertCircle size={40} color="#f87171" />
          <span className="gm-error-title">Failed to Load</span>
          <span className="gm-error-msg">{fetchError}</span>
          <button className="gm-btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  if (!form) return null;

  // ── Render ──
  return (
    <div className="em-overlay">
      <div className="em-backdrop" onClick={handleClose} />

      <div className="em-modal">
        {/* ── Header ── */}
        <div className="em-header">
          <div className="em-header-left">
            <h2 className="em-title">Edit Trading Group</h2>
            <div className="em-header-badges">
              {isDefault && (
                <span className="em-badge em-badge-default">
                  <Star size={13} /> Default Group
                </span>
              )}
              {changed && (
                <span className="em-badge em-badge-modified">
                  <span className="em-badge-dot" /> Modified
                </span>
              )}
            </div>
          </div>
          <button onClick={handleClose} disabled={saving} className="em-close-btn" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {/* ── Submit Error Banner ── */}
        {submitError && (
          <div className="em-error-banner">
            <AlertCircle size={16} />
            {submitError}
          </div>
        )}

        {/* ── Body ── */}
        <div className="em-body">

          {/* SECTION 1: Basic Info */}
          <section className="em-section">
            <div className="em-section-header">
              <Hash size={18} />
              <h3 className="em-section-title">Basic Information</h3>
            </div>
            {isDefault && (
              <div className="em-info-banner">
                <AlertCircle size={14} />
                Name and description are locked for the <strong>Default Group</strong>.
              </div>
            )}
            <div className="em-field">
              <label className="em-label">
                Group Name {!isDefault && <span className="em-required">*</span>}
              </label>
              <input
                type="text"
                value={form.name}
                onChange={e => updateField('name', e.target.value)}
                disabled={saving || isDefault}
                placeholder="Enter group name"
                className={`${fieldClass(original?.name, form.name)} ${fieldErrors.name ? 'em-input-error' : ''}`}
              />
              {fieldErrors.name && <span className="em-field-error">{fieldErrors.name}</span>}
            </div>
            <div className="em-field">
              <label className="em-label">Description</label>
              <textarea
                value={form.description}
                onChange={e => updateField('description', e.target.value)}
                disabled={saving || isDefault}
                placeholder="Describe the purpose of this trading group"
                rows={3}
                className={`${fieldClass(original?.description, form.description)} em-textarea ${fieldErrors.description ? 'em-input-error' : ''}`}
              />
              {fieldErrors.description && <span className="em-field-error">{fieldErrors.description}</span>}
            </div>
          </section>

          {/* SECTION 2: Leverage */}
          <section className="em-section">
            <div className="em-section-header">
              <BarChart3 size={18} />
              <h3 className="em-section-title">Leverage Settings</h3>
            </div>
            <div className="em-lev-grid">
              {ASSET_CATEGORIES.map(({ key, label, icon }) => {
                const presetOpts = getPresetOptions(key);
                const settings = form.leverageSettings[key];
                const origSettings = original?.leverageSettings?.[key];
                const maxErr = fieldErrors[`lev_${key}_maxLeverage`];
                const curErr = fieldErrors[`lev_${key}_currentLeverage`];

                return (
                  <div key={key} className="em-lev-card">
                    <div className="em-lev-card-header">
                      <span className="em-lev-icon">{icon}</span>
                      <span className="em-lev-label">{label}</span>
                      <span className="em-lev-max-tag">Max {CATEGORY_LEVERAGE_LIMITS[key]}:1</span>
                    </div>

                    {/* Max Leverage */}
                    <div className="em-lev-row">
                      <span className="em-lev-row-label">Max Leverage</span>
                      <div className="em-chip-group">
                        {presetOpts.map(v => (
                          <button
                            key={`max-${v}`}
                            type="button"
                            disabled={saving}
                            className={`em-chip ${settings?.maxLeverage === v ? 'em-chip-selected' : ''} ${origSettings?.maxLeverage !== v && settings?.maxLeverage === v ? 'em-chip-changed' : ''}`}
                            onClick={() => updateLeverage(key, 'maxLeverage', v)}
                          >
                            {v}:1
                          </button>
                        ))}
                      </div>
                      {maxErr && <span className="em-field-error">{maxErr}</span>}
                    </div>

                    {/* Default Leverage */}
                    <div className="em-lev-row">
                      <span className="em-lev-row-label">Default Leverage</span>
                      <div className="em-chip-group">
                        {presetOpts.filter(v => v <= (settings?.maxLeverage || 1)).map(v => (
                          <button
                            key={`cur-${v}`}
                            type="button"
                            disabled={saving}
                            className={`em-chip ${settings?.currentLeverage === v ? 'em-chip-selected' : ''} ${origSettings?.currentLeverage !== v && settings?.currentLeverage === v ? 'em-chip-changed' : ''}`}
                            onClick={() => updateLeverage(key, 'currentLeverage', v)}
                          >
                            {v}:1
                          </button>
                        ))}
                      </div>
                      {curErr && <span className="em-field-error">{curErr}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* SECTION 3: Trading Parameters */}
          <section className="em-section">
            <div className="em-section-header">
              <Settings size={18} />
              <h3 className="em-section-title">Trading Parameters</h3>
            </div>
            <div className="em-trade-grid">

              {/* Commission */}
              <div className="em-trade-card">
                <div className="em-trade-card-row">
                  <label className="em-label">Commission Rate</label>
                  <div className="em-number-input-wrap">
                    <input
                      type="number"
                      min="0"
                      max="0.01"
                      step="0.0001"
                      value={form.commission}
                      onChange={e => updateField('commission', parseFloat(e.target.value) || 0)}
                      disabled={saving}
                      className={`em-number-input ${fieldClass(original?.commission, form.commission)} ${fieldErrors.commission ? 'em-input-error' : ''}`}
                    />
                    <span className="em-number-suffix">{(form.commission * 100).toFixed(4)}%</span>
                  </div>
                </div>
                {fieldErrors.commission && <span className="em-field-error">{fieldErrors.commission}</span>}
              </div>

              {/* Swap Rate */}
              <div className="em-trade-card">
                <div className="em-trade-card-row">
                  <label className="em-label">Swap Rate</label>
                  <div className="em-number-input-wrap">
                    <input
                      type="number"
                      min="0"
                      max="0.01"
                      step="0.0001"
                      value={form.swap}
                      onChange={e => updateField('swap', parseFloat(e.target.value) || 0)}
                      disabled={saving}
                      className={`em-number-input ${fieldClass(original?.swap, form.swap)} ${fieldErrors.swap ? 'em-input-error' : ''}`}
                    />
                    <span className="em-number-suffix">{(form.swap * 100).toFixed(4)}%</span>
                  </div>
                </div>
                {fieldErrors.swap && <span className="em-field-error">{fieldErrors.swap}</span>}
              </div>

              {/* Swap Toggle */}
              <div className="em-trade-card">
                <div className="em-trade-card-row">
                  <label className="em-label">Swap Charges</label>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => updateField('swapEnabled', !form.swapEnabled)}
                    className={`em-toggle ${form.swapEnabled ? 'em-toggle-on' : 'em-toggle-off'} ${isFieldChanged(original?.swapEnabled, form.swapEnabled) ? 'em-toggle-changed' : ''}`}
                  >
                    <span className="em-toggle-knob" />
                  </button>
                </div>
              </div>

              {/* Conditional: Swap Time & Triple Swap Day */}
              {form.swapEnabled && (
                <div className="em-animate-in">
                  <div className="em-trade-card">
                    <div className="em-trade-card-row">
                      <label className="em-label">
                        <Clock size={14} style={{ marginRight: 4 }} />
                        Swap Time (UTC)
                      </label>
                      <input
                        type="text"
                        placeholder="22:00"
                        value={form.swapTime || ''}
                        onChange={e => updateField('swapTime', e.target.value)}
                        disabled={saving}
                        className={`em-input em-input-sm ${fieldClass(original?.swapTime, form.swapTime)} ${fieldErrors.swapTime ? 'em-input-error' : ''}`}
                      />
                    </div>
                    {fieldErrors.swapTime && <span className="em-field-error">{fieldErrors.swapTime}</span>}
                  </div>

                  <div className="em-trade-card">
                    <div className="em-trade-card-row">
                      <label className="em-label">Triple Swap Day</label>
                      <div className="em-select-wrap">
                        <select
                          value={form.tripleSwapDay || 'Wednesday'}
                          onChange={e => updateField('tripleSwapDay', e.target.value)}
                          disabled={saving}
                          className={`em-select ${fieldClass(original?.tripleSwapDay, form.tripleSwapDay)}`}
                        >
                          {SWAP_DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                        <ChevronDown size={14} className="em-select-arrow" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Default Spread */}
              <div className="em-trade-card">
                <div className="em-trade-card-row">
                  <label className="em-label">Default Spread</label>
                  <div className="em-number-input-wrap">
                    <input
                      type="number"
                      min="0"
                      max="0.001"
                      step="0.00001"
                      value={form.defaultSpread}
                      onChange={e => updateField('defaultSpread', parseFloat(e.target.value) || 0)}
                      disabled={saving}
                      className={`em-number-input ${fieldClass(original?.defaultSpread, form.defaultSpread)} ${fieldErrors.defaultSpread ? 'em-input-error' : ''}`}
                    />
                    <span className="em-number-suffix">{(form.defaultSpread * 100).toFixed(5)}%</span>
                  </div>
                </div>
                {fieldErrors.defaultSpread && <span className="em-field-error">{fieldErrors.defaultSpread}</span>}
              </div>

              {/* Max Position Size */}
              <div className="em-trade-card">
                <div className="em-trade-card-row">
                  <label className="em-label">Max Position Size</label>
                  <div className="em-number-input-wrap">
                    <span className="em-number-prefix">$</span>
                    <input
                      type="number"
                      min="1000"
                      max="10000000"
                      step="1000"
                      value={form.maxPositionSize}
                      onChange={e => updateField('maxPositionSize', parseFloat(e.target.value) || 0)}
                      disabled={saving}
                      className={`em-number-input ${fieldClass(original?.maxPositionSize, form.maxPositionSize)} ${fieldErrors.maxPositionSize ? 'em-input-error' : ''}`}
                    />
                  </div>
                </div>
                {fieldErrors.maxPositionSize && <span className="em-field-error">{fieldErrors.maxPositionSize}</span>}
              </div>

            </div>
          </section>

        </div>{/* /em-body */}

        {/* ── Footer ── */}
        <div className="em-footer">
          <div>
            {changed && (
              <button onClick={handleReset} disabled={saving} className="em-btn-reset">
                <RotateCcw size={15} /> Reset Changes
              </button>
            )}
          </div>
          <div className="em-footer-right">
            <button onClick={handleClose} disabled={saving} className="gm-btn-secondary">Cancel</button>
            <button
              onClick={handleSubmit}
              disabled={saving || !changed}
              className="gm-btn-primary"
            >
              {saving ? (
                <><Loader2 size={15} className="em-spin" /> Saving…</>
              ) : (
                <><Save size={15} /> Save Changes</>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
